import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";

interface Project {
  id: number;
  title: string;
  description: string;
  image_url: string;
  year: number;
}

interface ProjectPoster {
  id: number;
  title: string;
  year: number;
  image_url: string;
}

interface Sponsor {
  id: number;
  image_url: string;
}

const FoundationPage = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectPosters, setProjectPosters] = useState<ProjectPoster[]>([]);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetch(`/api/projects?lang=${i18n.language}`)
      .then((res) => res.json())
      .then((data) => setProjects(data))
      .catch((err) => console.error("Project fetch error:", err));

    fetch(`/api/project_posters?lang=${i18n.language}`)
      .then((res) => res.json())
      .then((data) => setProjectPosters(data))
      .catch((err) => console.error("Project poster fetch error:", err));

    fetch("/api/sponsors")
      .then((res) => res.json())
      .then((data) => setSponsors(data))
      .catch((err) => console.error("Sponsor fetch error:", err));
  }, [i18n.language]);

  return (
          <section className="w-full flex justify-center px-4 py-1 mt-8 bg-black text-white">
      <SEO
        title={t("pageTitles.foundation")}
        description="Internationale in Kunst - Berlin'de tiyatro ve sanat derneği. 2011'den beri kültürel projeler ve sanat etkinlikleri düzenliyoruz."
        keywords="tiyatro derneği, sanat derneği, berlin dernek, kunst verein, theater verein"
      />
      <div className="w-full max-w-6xl text-center">
        {/* Headline */}
        <h1 className="text-2xl sm:text-3xl font-extrabold underline underline-offset-8 mb-8 decoration-brand-red-text">
          {t("foundation.title")}
        </h1>

        {/* Intro - with <br /> support */}
        <p
          className="leading-relaxed text-center text-sm sm:text-base px-2 sm:px-6 md:px-12 mb-16"
          dangerouslySetInnerHTML={{ __html: t("foundation.intro") }}
        />

        {/* Sponsors Section */}
        {sponsors.length > 0 && (
          <>
            <h3 className="text-2xl sm:text-3xl font-extrabold underline underline-offset-8 decoration-brand-red-text mt-8 mb-8">
              {t("foundation.sponsors")}
            </h3>

            <div className="mb-16">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 md:gap-12 max-w-6xl mx-auto">
                {sponsors.map((sponsor) => (
                  <div 
                    key={sponsor.id}
                    className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-8 sm:p-10 border border-brand-red-text/60 shadow-2xl"
                  >
                    <div className="aspect-[2/1] flex items-center justify-center">
                      <img
                        src={sponsor.image_url}
                        alt="Sponsor Logo"
                        className="max-w-full max-h-full object-contain transition-all duration-300"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

                {/* Project Posters Gallery */}
        {projectPosters.length > 0 && (
          <>
            <h3 className="text-2xl sm:text-3xl font-extrabold underline underline-offset-8 decoration-brand-red-text mt-8 mb-8">
              {t("foundation.projectPosters")}
            </h3>

            <div className="mb-16 w-full relative">
              <div
                id="poster-gallery"
                className="overflow-x-auto scrollbar-hide scroll-smooth"
                style={{ scrollBehavior: 'smooth' }}
              >
                <div className="flex gap-8 sm:gap-10 pb-4 px-4 sm:px-6" style={{ width: 'max-content' }}>
                  {projectPosters.map((poster) => (
                                        <div
                      key={`poster-${poster.id}`}
                      className="relative flex-shrink-0 w-[160px] sm:w-[180px] md:w-[200px] lg:w-[220px] xl:w-[300px]"
                    >
                      {/* Poster */}
                      <div className="w-full aspect-[2/3] rounded-xl overflow-hidden shadow-lg">
                        <img
                          src={poster.image_url}
                          alt={poster.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>

                      {/* Title & Year */}
                      <div className="mt-3 text-center">
                        <h4 className="text-white text-xs sm:text-sm font-semibold line-clamp-2">
                          {poster.title}
                        </h4>
                        <p className="text-gray-400 text-xs mt-1">
                          ({poster.year})
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Arrows */}
              {projectPosters.length > 3 && (
                <>
                  <button
                    onClick={() => {
                      const gallery = document.getElementById('poster-gallery');
                      if (gallery) gallery.scrollLeft -= 300;
                    }}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white hover:text-brand-red-text transition-all duration-200 shadow-lg hover:shadow-xl opacity-0 lg:opacity-100 pointer-events-none lg:pointer-events-auto cursor-pointer"
                    aria-label="Previous posters"
                  >
                    <FaChevronLeft className="text-lg" />
                  </button>

                  <button
                    onClick={() => {
                      const gallery = document.getElementById('poster-gallery');
                      if (gallery) gallery.scrollLeft += 300;
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white hover:text-brand-red-text transition-all duration-200 shadow-lg hover:shadow-xl opacity-0 lg:opacity-100 pointer-events-none lg:pointer-events-auto cursor-pointer"
                    aria-label="Next posters"
                  >
                    <FaChevronRight className="text-lg" />
                  </button>
                </>
              )}

              {/* Gradient fade effects */}
              <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-black to-transparent pointer-events-none opacity-0 lg:opacity-100" />
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-black to-transparent pointer-events-none opacity-0 lg:opacity-100" />
            </div>
          </>
        )}

        {/* Project Headline */}
        <h3 className="text-2xl sm:text-3xl font-extrabold underline underline-offset-8 decoration-brand-red-text mt-8 mb-12">
          {t("foundation.projects")}
        </h3>

        {/* Slides */}
        <div className="relative w-full flex flex-col items-center mt-8 sm:mt-10 md:mt-12">
          <div className="w-[95%] sm:w-[85%] md:w-[80%] lg:max-w-4xl rounded-lg overflow-hidden shadow-lg relative">
            {projects.length > 0 && (
              <Swiper
                modules={[Pagination, Navigation]}
                pagination={{ clickable: true, el: ".custom-swiper-pagination" }}
                navigation={{
                  nextEl: ".custom-swiper-button-next",
                  prevEl: ".custom-swiper-button-prev",
                }}
                loop={true}
                onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                className="w-full"
              >
                {projects.map((project, idx) => (
                  <SwiperSlide key={project.id || idx}>
                    <div className="w-full aspect-[3/2] rounded-lg overflow-hidden">
                      <img
                        src={project.image_url}
                        alt={project.title}
                        className="w-full h-full object-cover filter brightness-75"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>

          {/* Arrows */}
          <div className="hidden lg:block">
            <div className="custom-swiper-button-prev absolute top-1/2 left-[3.5%] transform -translate-y-1/2 z-20 cursor-pointer
              opacity-0 md:opacity-100 pointer-events-none md:pointer-events-auto transition-opacity duration-300">
              <div className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white hover:text-brand-red-text transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer">
                <FaChevronLeft className="text-lg" />
              </div>
            </div>

            <div className="custom-swiper-button-next absolute top-1/2 right-[3.5%] transform -translate-y-1/2 z-20 cursor-pointer
              opacity-0 md:opacity-100 pointer-events-none md:pointer-events-auto transition-opacity duration-300">
              <div className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white hover:text-brand-red-text transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer">
                <FaChevronRight className="text-lg" />
              </div>
            </div>
          </div>
        </div>

        {/* Pagination bullets */}
        <div className="custom-swiper-pagination flex justify-center mt-4" />

        {/* Title + Description */}
        {projects.length > 0 && (
          <div className="mt-4 sm:mt-6 md:mt-8 px-4 sm:px-6 md:px-10 text-center max-w-3xl mx-auto">
            <h4 className="text-lg sm:text-xl font-semibold text-white">
              {projects[activeIndex].title}{" "}
              <span className="text-gray-300">({projects[activeIndex].year})</span>
            </h4>
            <p className="mt-2 text-gray-300 leading-relaxed">
              {projects[activeIndex].description}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FoundationPage;